import { z } from 'zod';
import { BagPhotoPolicy } from '../bag-photos';
import {
  CurrencyCode,
  DeviceKind,
  DevicePin,
  Email,
  Instant,
  LocalTime,
  PhoneE164,
  ServiceDay,
  TimeZone,
  Uuid,
} from '../common';
import { ServicePauseReason } from '../enums';
import {
  DEVICE_COMMANDS_PER_BEAT,
  DeviceCommand,
  DeviceCommandAck,
  DeviceCommandOutcome,
} from '../handheld-commands';
import { HandheldConfig } from '../handheld-config';
import {
  HandheldCrashAccepted,
  HandheldCrashReport,
  HandheldDiagnosticsAccepted,
  HandheldDiagnosticsBundle,
} from '../handheld-diagnostics';
import { HandheldUpdateOffer, HandheldVersionCodeParam } from '../handheld-release';
import {
  HANDHELD_FAILURES_PER_BEAT,
  HandheldRequestFailure,
  HandheldTelemetry,
} from '../handheld-telemetry';
import { defineEndpoint } from '../http';
import { COUNTER_INTAKE_ACTIONS, COUNTER_INTAKE_REASONS } from '../projections/counter-intake';

// ---------------------------------------------------------------------------
// Device session and liveness (PRD BAK-020, BAK-022, BAK-023, BAK-025)
// ---------------------------------------------------------------------------

/**
 * PRD BAK-063 - can this counter receive a new order right now, and if not,
 * why. Derived by `counterIntakeOf` (`projections/counter-intake.ts`) from the
 * facts allocation itself reads; the worker gathers them and decides nothing.
 *
 * The device decides nothing either. `open` is green or grey on the board,
 * `actions` is whether Reprendre is drawn, and `reason` only chooses the words:
 * a reason an older build has never heard of is still grey and still says the
 * shop cannot take orders, so a new reason needs no APK to be shown truthfully.
 *
 * The two suspensions are not reasons here, because no context reaches a
 * suspended counter: every `/b` call answers them with a 403 whose code
 * (`org_suspended`, `location_suspended`) is their whole contract.
 */
export const CounterIntakeReason = z.enum(COUNTER_INTAKE_REASONS);
export const CounterIntake = z.object({
  open: z.boolean(),
  reason: CounterIntakeReason,
  /** When it lifts by itself; null when a person has to lift it. */
  reopensAt: Instant.nullable(),
  /** The last day of a recorded closure run, when that day is known. */
  closedThrough: ServiceDay.nullable(),
  /** RESUME while a pause exists, PAUSE otherwise - never both. */
  actions: z.array(z.enum(COUNTER_INTAKE_ACTIONS)),
});
export type CounterIntake = z.infer<typeof CounterIntake>;

export const BakeryContext = z.object({
  organizationId: Uuid,
  locationId: Uuid,
  locationName: z.string().max(160),
  timeZone: TimeZone,
  currency: CurrencyCode,
  /** PRD BAK-027 - the single lead-time setting; drives the whole morning. */
  earliestReadyLocalTime: LocalTime,
  serviceDay: ServiceDay,
  acceptanceMode: z.enum(['AUTO', 'MANUAL']),
  /**
   * THE ORDER CAMERA (founder, 2026-10-08): whether this counter photographs
   * the bag at Ready. OFF draws the Ready flow as it always was; OPTIONAL
   * offers "Passer"; MANDATORY does not, and the platform refuses a SKIPPED
   * Ready with `409 bag_photo_required`. Always present; set by operations and
   * the house, never by the counter (`PATCH /b/settings` cannot carry it).
   */
  bagPhotoPolicy: BagPhotoPolicy,
  /**
   * PRD BAK-006 - this counter has stopped taking orders. THE BOOLEAN IS THE
   * FACT; the instant beside it is only its end.
   *
   * "Pause until I resume" carries no end, so `ordersPausedUntil` is null for a
   * paused counter and null for one that was never paused - the same two bytes
   * for the opposite states. The tablet read the instant, so the one control
   * BAK-006 gives a baker could not be read back on the baker's own screen:
   * the header cleared, the banner went, and the counter looked open while the
   * guest was being turned away. `/c/locations` has always answered the
   * boolean (`paused`), which is the two-mounts-one-fact split BAK-028 forbids.
   */
  ordersPaused: z.boolean(),
  ordersPausedUntil: Instant.nullable(),
  /**
   * PRD BAK-038 - WHO paused this counter, and why. Null when it is not paused.
   *
   * `MANUAL` is the baker's own tap. The two `UNATTENDED_` values are the
   * platform pausing an unattended counter, and the counter must be told: an
   * indefinite pause nobody at the shop asked for looks exactly like a bug
   * unless the screen says the platform did it and offers Resume.
   */
  pauseReason: ServicePauseReason.nullable(),
  /** PRD OPS-009 - the kill switch reached this location. */
  servicePaused: z.boolean(),
  /**
   * HOW LOUD a new order is on this counter, decided by the INSTANCE and never
   * by the device (docs/plans/guest-ordering-scenarios.html 3B).
   *
   * `LOUD` is the alarm BAK-022 describes: the tone, the volume floor, the
   * repeat. `GENTLE` is what every non-production instance answers - a
   * rehearsal on demo places real orders on the founder's real Sunmis, at hours
   * no bakery trades at. On the handheld it is that SAME alarm at the alarm
   * stream's smallest audible step (founder decision 2026-08-30, replacing the
   * single vibration it was): quiet enough not to wake a house at 03:00, and
   * still the alarm, because demo and the emulator are the only places anybody
   * can check it by ear. The browser tablet plays one vibration and one soft
   * note.
   *
   * OPTIONAL ON THE WIRE, and absent means LOUD on both devices. A handheld is
   * installed by cable and cannot be hot-fixed, so a build older than this
   * field keeps the alarm it shipped with rather than losing it; the server
   * always sets the field.
   */
  alarmProfile: z.enum(['LOUD', 'GENTLE']).optional(),
  /**
   * IS THE ALARM SILENT FOR THE RUN IN PROGRESS - no tone, no vibration
   * (BAK-022, and docs/plans/guest-ordering-scenarios.html 3B).
   *
   * A SEPARATE FIELD RATHER THAN A THIRD `alarmProfile` VALUE, and that is the
   * whole reason it exists in this shape. Both devices resolve anything that is
   * not `GENTLE` to `LOUD` - deliberately, so an unknown word can never be a
   * reason to stop making a sound - so a `SILENT` added to that enum would
   * reach every counter running a build older than it and ring the FULL alarm:
   * the loudest possible answer to a request for silence. An unknown FIELD is
   * ignored by those same builds, which leaves them on the profile beside it,
   * and outside production that profile is `GENTLE`. The failure is quiet.
   *
   * NOT A SETTING AND NOT A DEVICE TOGGLE. It is true only while a rehearsal
   * has asked for it (`POST /dev/rehearsal/alarm`, and `scenario.mjs --silent`
   * clears it in its teardown beside the clock), it is unreachable in
   * production, and no operator screen offers it. `GENTLE` remains what demo
   * answers by default, because quiet is checkable by ear and silent is not -
   * this is for the one case that decision does not cover, a scenario played at
   * an hour when the founder's Sunmis are in a house full of people asleep.
   *
   * OPTIONAL ON THE WIRE, and absent means not silenced.
   */
  alarmMuted: z.boolean().optional(),
  /**
   * HOW MANY orders on this counter's own service day are still waiting for a
   * decision - the New lane, counted (PRD BAK-057).
   *
   * The context is the only thing a LOCKED device reads. Both apps stop asking
   * for the queue behind the keypad and behind the update offer, so an order
   * arriving there was neither shown nor heard until somebody unlocked - which
   * is the gap BAK-001 does not allow. This field is what the lock screen rings
   * and counts from, and it is the SAME derivation the board's New lane uses
   * (`bakeryQueueOf`), never a second one that could disagree with the lane a
   * baker sees a second later.
   *
   * OPTIONAL ON THE WIRE, and absent means zero. A handheld is installed by
   * cable and cannot be hot-fixed, so a build older than this field goes on
   * working exactly as it did; the server always sets it.
   */
  awaitingCount: z.number().int().nonnegative().optional(),
  /**
   * PRD BAK-063 - whether this counter can receive a new order, and why not
   * (`CounterIntake` above).
   *
   * OPTIONAL ON THE WIRE, and absent means the device reads `ordersPaused`,
   * `pauseReason` and `servicePaused` exactly as it did before the field
   * existed. A handheld is installed by cable and cannot be hot-fixed, so a
   * build newer than its worker must still draw a truthful board; the server
   * always sets it, and the fields above stay for the builds that predate it.
   */
  intake: CounterIntake.optional(),
});
export type BakeryContext = z.infer<typeof BakeryContext>;

/**
 * The address the counter reads on its lock screen and its pairing
 * confirmation, before the network has answered anything else
 * (docs/plans/device-pairing.html 3 and 4).
 *
 * Full, and not a formatted line: the Sunmi prints it on the pairing ticket and
 * the tablet paints it above the keypad, and each lays it out its own way.
 */
export const DeviceLocationIdentity = z.object({
  id: Uuid,
  name: z.string().min(1).max(160),
  addressLine1: z.string().min(1).max(200),
  addressLine2: z.string().max(200).nullable(),
  postalCode: z.string().min(1).max(20),
  city: z.string().min(1).max(100),
});
export type DeviceLocationIdentity = z.infer<typeof DeviceLocationIdentity>;

/**
 * Pairing: the QR chooses the shop, and choosing it sets a new PIN
 * (device-pairing plan 2.3, founder decision 2026-08-23).
 *
 * NO AUTH, deliberately - the code IS the credential, and a device holding one
 * has nothing else yet. What keeps that safe is entropy and revocability: 10
 * Crockford characters is ~50 bits, and the answer to a code that leaked or was
 * photographed off a wall is Regenerate, which kills it.
 *
 * **Pairing never asks for the shop's existing PIN.** It used to: the first
 * device paired on the code alone and every later one had to supply the four
 * digits too. That rule made the commonest support call unanswerable by the
 * only people present - a counter whose staff have all forgotten the code, with
 * the shop's own QR on the wall in front of them, could not use it. So
 * scanning the QR IS the reset now: every pairing marks this device as owing a
 * PIN, answers `pinRequired: true`, and the app takes the counter through
 * choosing a new one before the board.
 *
 * The consequence was accepted rather than overlooked: re-pairing ONE device
 * changes the PIN for every device at that counter. It is announced - the
 * pairing e-mail to the location's registered contact says a new code is being
 * chosen on the device it names.
 *
 * Not idempotent, and it needs no key: a replay re-issues the same device's
 * token onto the same row (the upsert is on `(location, deviceId)`), which is
 * exactly what a retry after a dropped response should do.
 */
export const pairDevice = defineEndpoint({
  method: 'POST',
  path: '/b/devices/pair',
  mount: 'bakery',
  summary: 'Pair a counter device against its shop QR code; every pairing sets a new PIN',
  body: z.object({
    /** Minted by the app itself and kept across restarts; never typed in. */
    deviceId: z.string().min(8).max(128),
    /**
     * The 10 Crockford base32 characters off the QR (or typed on the grid).
     * The CLIENT strips the grouping dashes and upper-cases before sending, so
     * what arrives is the code itself - one representation on the wire.
     */
    code: z.string().length(10),
    kind: DeviceKind,
    label: z.string().min(1).max(60).optional(),
    appVersion: z.string().min(1).max(40).optional(),
    /**
     * `Build.MODEL`, as telemetry later reports it. Sent at pairing because the
     * platform NAMES the device here, and an emulator is numbered in a series of
     * its own (`isEmulatorModel`). Optional: a build that predates it sends
     * none and is numbered as a machine, which is what it was before.
     */
    model: z.string().max(60).optional(),
  }),
  idempotent: false,
  response: z.object({
    deviceToken: z.string().min(16).max(2048),
    /**
     * The handheld's FLEET credential (`fleetHeartbeat`), for a `SUNMI`
     * pairing and absent for any other kind: it outlives this pairing, so a
     * device removed from the shop is still seen and still updated. The app
     * keeps it apart from `deviceToken` and never clears it on an unpair.
     */
    fleetToken: z.string().min(16).max(2048).optional(),
    /**
     * Always true, and kept as a field rather than dropped: it is what both
     * counter apps already read to decide "choose a code before the board", and
     * a constant on the wire is cheaper than two app releases racing a contract
     * change. Pairing is now the only way a PIN is chosen from a device, so
     * there is no pairing that answers false.
     */
    pinRequired: z.literal(true),
    /**
     * What the fleet list calls this device, decided HERE and not by the
     * device: a new Sunmi is numbered by the platform (`Sunmi 00001`, one
     * running count per instance, never reused), a re-pairing keeps the name
     * its row already has, and a caller that sent a `label` gets it back. The
     * counter prints it on its Appareil screen so a sticker, the screen and
     * the row all read the same thing.
     */
    label: z.string().min(1).max(60),
    location: DeviceLocationIdentity,
    /**
     * The server's own clock. The counter keeps the offset and evaluates
     * BAK-025's 05:30-11:00 window against corrected time, because a tablet
     * whose clock is wrong would otherwise lock itself mid-service.
     */
    serverNow: Instant,
  }),
});

/**
 * What shop is behind this code, before anything is paired to it (plan 4).
 *
 * NO AUTH, like `pairDevice`, and for the same reason: the `/pair/<code>` page
 * has to print the shop's name and address ABOVE the button, so the person
 * holding a phone can see they are about to join the right bakery - a pairing
 * that is confirmed blind is one nobody can un-confirm.
 *
 * A read, so it writes no row and issues no token. An unknown code is 404
 * `unknown_pairing_code` with no body detail, exactly as pairing answers one:
 * the two must be indistinguishable, or the cheaper of the two becomes a way to
 * enumerate the platform's bakeries.
 */
export const lookupPairingCode = defineEndpoint({
  method: 'GET',
  path: '/b/devices/pairing/:code',
  mount: 'bakery',
  summary: 'Name and address behind a pairing code, for the confirm step; no auth, no write',
  params: z.object({ code: z.string().min(1).max(40) }),
  response: z.object({
    location: DeviceLocationIdentity,
    /**
     * Which contacts this shop has on file, the same booleans `getContext`
     * carries and for the same reason: a screen has to know whether "send it to
     * us" is possible before it offers the button. Here it is the confirm step
     * that asks, because that screen is reached with no device token at all -
     * a phone that has scanned a QR and nothing else.
     *
     * Booleans and never the addresses. This endpoint has NO AUTH, so anything
     * it answers is answered to whoever holds ten characters; "an e-mail exists"
     * is worth nothing to them and the address itself would be worth a great
     * deal.
     */
    contact: z.object({ email: z.boolean(), mobile: z.boolean() }),
  }),
});

/**
 * The counter chooses the shop's PIN (plan 2.4).
 *
 * Three ways to be entitled to, and they are not the same decision:
 *
 *  - THIS DEVICE HAS JUST PAIRED, which is now every pairing. It always wins:
 *    the counter that scanned the QR to get back in cannot be told that some
 *    other device already chose a code, because that is the state it is
 *    escaping. The code it types replaces whatever the shop had;
 *  - an admin's reset is pending, or no PIN is stored at all. That is a
 *    compare-and-swap server-side, so two devices asked at the same moment do
 *    not each set one - the loser is answered 409 `pin_already_set` and shown
 *    "un code a deja ete choisi sur un autre appareil". The 409 is ONLY this
 *    race now; a freshly paired device never sees it;
 *  - nothing else. A paired device that has finished its setup and is past a
 *    reset has no way in here, and is sent to the lock screen.
 *
 * Idempotent, and the app mints the key: this is the one PIN endpoint where a
 * retry after a dropped response must not be read as a second attempt.
 * Confirmation ("type it again") is the APP's job - the server takes one value.
 */
export const setDevicePin = defineEndpoint({
  method: 'POST',
  path: '/b/devices/pin',
  mount: 'bakery',
  summary: 'Choose this shop PIN from a paired device; a device that just paired always wins',
  body: z.object({ pin: DevicePin }),
  idempotent: true,
  response: z.object({ pinSetAt: Instant }),
});

/**
 * The lock screen, answered (plan 2.3).
 *
 * Mints NO token: a locked device is not distinguished from an unlocked one
 * server-side, and its token keeps answering `/b/context` and the heartbeat
 * throughout. The PIN stops a passer-by at the counter from tapping a ticket;
 * what protects the API is the device row. This is stated here so nobody later
 * builds server-side "unlock state" to defend something the PIN never defended.
 *
 * It replies the same context `getContext` does plus `serverNow`, so the
 * tablet's existing adopt path is unchanged.
 */
export const unlockDevice = defineEndpoint({
  method: 'POST',
  path: '/b/session/unlock',
  mount: 'bakery',
  summary: 'Unlock the counter screen with the shop PIN; mints no token (BAK-025)',
  body: z.object({ pin: DevicePin }),
  idempotent: false,
  response: z.object({
    context: BakeryContext,
    serverNow: Instant,
  }),
});

/**
 * The counter unpairs its own device - the log-out this product never had
 * (plan 2.3, "Dissocier cet appareil").
 *
 * PIN required, because it is the one action on the device that cannot be
 * undone from the device. Reachable during service too: the lock screen is not,
 * and it is the shop's own hardware, so the app warns ("le service est en
 * cours") rather than refusing. Same row update and same audit entry as an
 * admin's revoke.
 */
export const revokeSelfDevice = defineEndpoint({
  method: 'POST',
  path: '/b/devices/self/revoke',
  mount: 'bakery',
  summary: 'Unpair this device from its shop; the token dies on its next call (BAK-037)',
  body: z.object({ pin: DevicePin }),
  idempotent: false,
  response: z.object({ revokedAt: Instant }),
});

/**
 * The counter asks for its own PIN back, from the lock screen (BAK-041).
 *
 * The one endpoint on this mount that sends a credential OUT, so every rule
 * around it is about where it may go:
 *
 *  - the destination is the shop's REGISTERED contact for that channel and
 *    nothing else. The device does not get to name one - a device at a counter
 *    is whoever is standing at the counter, and an address in this body would
 *    make "forgot the PIN?" a way to mail it to a phone in a pocket;
 *  - a shop with no contact for that channel is 400 `no_contact` and a shop
 *    with no PIN is 400 `no_pin`, rather than a send that quietly goes nowhere;
 *  - one per ten minutes per LOCATION, 429 `too_many_requests` carrying
 *    `until` - the throttle is on the shop, not the device, for the same reason
 *    the PIN one is;
 *  - the reply names the destination MASKED (`j***@gmail.com`), because unlike
 *    the operator's dialog this answer is painted on a screen at a public
 *    counter. It is enough to recognise the manager's own address and not
 *    enough to learn it.
 */
export const requestDevicePin = defineEndpoint({
  method: 'POST',
  path: '/b/devices/pin/request',
  mount: 'bakery',
  summary: 'Send this shop PIN to its registered contact, asked for by the counter (BAK-036)',
  body: z.object({ channel: z.enum(['EMAIL', 'SMS']) }),
  idempotent: false,
  response: z.object({
    /** Masked: `j***@gmail.com`, `+33 6 ** ** ** 12`. Never the whole address. */
    sentTo: z.string().min(1).max(200),
  }),
});

/**
 * The platform's support line, for the two screens a counter reaches when
 * nothing else on this mount will answer it.
 *
 * NO AUTH, deliberately, and it is the reason this is an endpoint rather than a
 * field on `getContext`: the screens that need it are the not-paired screen and
 * the "this code is not recognized" one, where there is no device token to ask
 * with. Both apps fetch it once per launch and keep it.
 *
 * Null when `SUPPORT_PHONE` is unset - the apps then show no line at all,
 * rather than a number somebody wrote into a build. `email` reads the same way
 * off `PARTNER_SUPPORT_EMAIL` - a counter is a business partner, so it is the
 * partner inbox it is given, never the guest one - and both being null is the
 * state where the help block disappears entirely.
 */
export const getSupport = defineEndpoint({
  method: 'GET',
  path: '/b/support',
  mount: 'bakery',
  summary: 'How a counter reaches a human; configuration, never compiled into an app',
  response: z.object({
    phone: PhoneE164.nullable(),
    /**
     * The address offered under the number, for a baker who cannot ring - a
     * noisy shop, a call nobody answered. Null when `PARTNER_SUPPORT_EMAIL` is
     * unset, and the apps then show the number alone.
     */
    email: Email.nullable(),
  }),
});

/**
 * What this device is, and what its counter is doing - the one read both apps
 * make on launch and poll on (device-pairing plan 2.3).
 *
 * It carries the location's NAME AND ADDRESS because the lock screen paints
 * them before anything else answers, and a counter that cannot say which shop
 * it belongs to is a counter nobody can debug at 05:30.
 */
export const getContext = defineEndpoint({
  method: 'GET',
  path: '/b/context',
  mount: 'bakery',
  summary: 'This device, its shop and its service state; polled by both counter apps (BAK-020)',
  response: z.object({
    context: BakeryContext,
    location: DeviceLocationIdentity,
    /**
     * The device row behind this token. Null for a session that is not a paired
     * device at all - the pilot cookie, the dev-header bypass - which is a real
     * state on this mount and not an error.
     */
    device: z
      .object({
        id: Uuid,
        label: z.string().min(1).max(60),
        pairedAt: Instant.nullable(),
      })
      .nullable(),
    /**
     * An admin has reset the PIN and this counter has not chosen the
     * replacement yet (plan 2.4). At its next lock OUTSIDE the 05:30-11:00
     * window the app shows "choisissez un nouveau code PIN" instead of the
     * keypad prompt; nothing happens during service, by BAK-025.
     */
    pinPending: z.boolean(),
    /**
     * THIS device paired and has not chosen the shop's code yet.
     *
     * Every pairing sets a new PIN, and the two screens that do it - "choose a
     * code", "type it again" - are the only thing between the pairing and the
     * board. An app killed in that gap (a Sunmi that ran out of battery, a
     * tablet somebody unplugged) would otherwise come back to a board it never
     * earned, behind a lock screen wanting a code nobody chose. So the fact
     * lives on the device row and is reported here: a relaunch reads it and
     * goes back to "choose a code".
     *
     * Distinct from `pinPending` above, which is the LOCATION's state after an
     * admin's reset and is true for every device at the counter. This one is
     * about the screen asking.
     */
    pinSetupPending: z.boolean(),
    /**
     * Which contacts this shop has on file, so the lock screen can offer
     * "recevoir le code par SMS" and "par e-mail" and DISABLE the one that
     * would be refused (BAK-041). Booleans and not the addresses: the counter
     * needs to know a send is possible, and nothing more - the destination is
     * chosen server-side and comes back masked.
     */
    contact: z.object({ email: z.boolean(), mobile: z.boolean() }),
    /**
     * When this counter's PIN keypad stops being locked out, or null. Polled
     * from here rather than remembered from the 403 that reported it, so a
     * device that was restarted, or one that was not the device that got the
     * shop locked, still shows the countdown.
     */
    lockedUntil: Instant.nullable(),
    /** Server clock; the app keeps the offset and never trusts its own. */
    serverNow: Instant,
  }),
});

export const heartbeat = defineEndpoint({
  method: 'POST',
  path: '/b/heartbeat',
  mount: 'bakery',
  summary: 'Device liveness; a missing heartbeat raises an operations alert (BAK-020, OPS-011)',
  body: z.object({
    audioContextHealthy: z.boolean(),
    wakeLockHeld: z.boolean(),
    /**
     * RETIRED 2026-08-30 (PRD BAK-023): tolerated for builds that still send
     * it, read by nothing.
     *
     * It reported that the app had been behind something else since the last
     * beat, and no surface ever printed it and no alert was ever raised on it -
     * the fact an operator can act on is the one `audioContextHealthy` carries
     * (COUNTER_MUTED, BAK-022): a counter whose alarm cannot sound. Backgrounded
     * is not that, because the arrival alarm rings from the watch service
     * whether or not a screen is in front (BAK-059, STANDBY).
     *
     * Optional rather than deleted, and it stays optional: this app is installed
     * by cable, a counter can be a build or two behind for weeks, and a required
     * field removed from the schema would 400 every beat from one of them - the
     * device would go dark to the platform for the one reason liveness exists to
     * catch. The senders stopped sending it in the same change.
     */
    backgroundedSinceLastBeat: z.boolean().optional(),
    /**
     * NOBODY IS LOOKING AT THIS COUNTER (PRD BAK-059, founder decision B,
     * 2026-08-30).
     *
     * True on the ONE beat a closed app sends from its periodic job
     * (`UpdateJob`, plan 4c): the app has no screen in front, and the beat
     * exists so a release lands on a counter nobody opened. False - and
     * ABSENT, which means the same - on a beat sent by the ticker, which runs
     * only while one of the app's screens is started.
     *
     * The platform reads it as a CADENCE, not as a state: a closed app beats
     * every fifteen minutes, an open one every thirty seconds, so the silence
     * that means "gone" is a different number for each. Without the field a
     * healthy closed counter crossed the ten-minute window between every pair
     * of job beats and flapped ONLINE - ASLEEP - ONLINE all night, raising
     * OPS-011 on each quiet stretch.
     *
     * Optional, and it stays optional: the browser tablet has no job and no
     * closed state, and a Sunmi installed before this field shipped only ever
     * beats from a screen - so absent is exactly the old meaning and is read
     * as an open app.
     */
    screenless: z.boolean().optional(),
    online: z.boolean(),
    queuedMutations: z.int().nonnegative(),
    /**
     * What is beating, and which build of it - this is where the operator's
     * Devices list reads both (device-pairing plan 3.1). Optional because a
     * heartbeat that carries no device identity at all (a manager signed in on
     * the location, the dev-header bypass) has neither to report, and because
     * a device installed before pairing shipped goes on beating without them
     * until its next update.
     */
    kind: DeviceKind.optional(),
    appVersion: z.string().min(1).max(40).optional(),
    /**
     * The same build as `appVersion`, as the number the SERVER compares.
     *
     * Two fields for one build, deliberately. The NAME is what a human reads
     * in the Devices list (`1.0.544+529a9f6b`, and `-dirty` when the tree
     * carried uncommitted work); the CODE is `BuildConfig.VERSION_CODE`, the
     * commit count Android itself enforces monotonically. Neither is ever
     * derived from the other - parsing that string back into an integer would
     * be a second source of truth for a number the build already knows, and
     * the `-dirty` suffix is exactly the case where the parse goes wrong.
     *
     * Optional, and it stays optional: the browser tablet has no version code
     * at all, and a Sunmi installed before this field shipped goes on beating
     * without it until its next update.
     *
     * Capped at 2100000000, which is ANDROID's own ceiling for `versionCode`
     * rather than a number chosen here: the Play Store and the package manager
     * both refuse anything above it, so a beat carrying more is reporting a
     * build that could not have been installed. The bound is what stops that
     * from becoming a number the server later compares against - a device
     * claiming 2^53 would win every "is this counter current?" test for ever,
     * and would do it from a field nothing validated.
     */
    appVersionCode: z.int().positive().max(2100000000).optional(),
    /**
     * What this counter reports about itself (plan 9).
     *
     * Optional and it stays optional: the browser tablet fills none of it -
     * every field is an Android system call - and a Sunmi installed before
     * telemetry shipped goes on beating without it. Absent leaves the stored
     * record as it was rather than blanking it, which is the same rule
     * `kind` and `appVersion` above already follow.
     */
    telemetry: HandheldTelemetry.optional(),
    /**
     * The requests this counter made since the last beat that did NOT work.
     *
     * The telemetry above is what the device is; this is what it just failed to
     * do, and only the second one answers "the counter said no connection while
     * a courier waited". A failure rides the beat the device already sends
     * rather than a channel of its own, and lands on the device's timeline as
     * `device.request_failed`.
     *
     * Optional and it stays optional, for the same two reasons `telemetry` is:
     * the browser tablet sends none, and a Sunmi installed before this field
     * shipped goes on beating without it. Capped rather than paged
     * (`HANDHELD_FAILURES_PER_BEAT`) - a counter whose network has gone fails
     * every request it makes, and the beat carrying that backlog must not be
     * the flood.
     */
    failures: z.array(HandheldRequestFailure).max(HANDHELD_FAILURES_PER_BEAT).optional(),
  }),
  idempotent: false,
  response: z.object({
    acknowledgedAt: Instant,
    /** Server-authoritative time so the tablet's countdowns cannot drift. */
    serverTime: Instant,
    /**
     * A replacement credential, on the ONE beat that moves this device onto
     * its permanent identity (BAK-037).
     *
     * A handheld installed before build 717 paired under a random id its
     * preferences minted; the hardware's own id (`telemetry.androidId`) is
     * what it presents from 717 on. When a beat reports one under a token
     * still naming the other, the platform renames the row to the hardware's
     * id and hands back a token that names it too. The device stores it and
     * beats with it from then on; nothing is asked of the counter. Absent on
     * every other beat, and a client that ignores it keeps working for the
     * minute the old token is still honoured.
     */
    deviceToken: z.string().min(1).optional(),
    /**
     * The handheld's FLEET credential (`fleetHeartbeat`), on every beat from a
     * `SUNMI` device and absent otherwise. Every beat rather than once, so a
     * counter paired before the fleet token existed holds one within a beat of
     * updating, and a removal can never catch it without one.
     */
    fleetToken: z.string().min(16).max(2048).optional(),
    /**
     * A build this counter should install, or null (handheld-fleet plan 3).
     *
     * The offer rides the beat the device already sends twice a minute rather
     * than a polling loop, a push channel or a store listing: the device asks
     * the question anyway, and the answer grows one field.
     *
     * Null for the browser tablet always, because it sends no
     * `appVersionCode` and there is nothing to compare - it is a web app and
     * updates itself by being reloaded. Null too for a Sunmi already at or
     * above the latest published build, and for one outside the rollout ring
     * the release is currently offered to (plan 12).
     */
    update: HandheldUpdateOffer.nullable(),
    /**
     * What this desk wants this counter to do, oldest first (plan 11).
     *
     * ALWAYS present, and EMPTY for the browser tablet, which has a handler for
     * none of these: a field that appears only sometimes is a field every
     * client has to guard, and an empty array is the honest answer for a device
     * nobody can ring. A command is answered on every beat until it is
     * acknowledged or expires, so a beat the device never finished processing
     * costs nothing but the next beat.
     */
    commands: z.array(DeviceCommand).max(DEVICE_COMMANDS_PER_BEAT),
    /**
     * The three configuration layers, already resolved into ONE object (plan
     * 11). Always present; the browser tablet ignores it. The device stores it,
     * applies it, and reports `config.version` back as
     * `telemetry.configVersion` on the beats that follow, which is how a
     * counter that has not caught up becomes visible rather than assumed.
     */
    config: HandheldConfig,
  }),
});

/**
 * The device's answer to one command (plan 11).
 *
 * Idempotent on the command id: a replayed ack - the retry of a beat whose
 * response was lost, a queue the app drained twice - answers 200 and changes
 * nothing, because the record keeps the FIRST outcome. A command addressed to
 * another device is 404 rather than 403: a counter is told nothing about the
 * existence of commands that are not its own.
 */
export const ackDeviceCommand = defineEndpoint({
  method: 'POST',
  path: '/b/commands/:commandId/ack',
  mount: 'bakery',
  summary: 'A counter reports how one command ended; idempotent on its id (OPS-029)',
  params: z.object({ commandId: Uuid }),
  body: DeviceCommandAck,
  /*
   * Deliberately NOT `idempotent: true`, which is the header-and-claim
   * mechanism. This endpoint's idempotency is INTRINSIC and stronger: the
   * command id in the path is the key, and the record keeps the first outcome
   * whatever a retry says. Demanding an `Idempotency-Key` on top would be a
   * second nonce for the device to mint, generate and remember - and a 400 for
   * the one client that forgot it.
   */
  idempotent: false,
  response: z.object({ acknowledgedAt: Instant, outcome: DeviceCommandOutcome }),
});

/**
 * The build itself, against the device token (plan 3, 16.4).
 *
 * On `/b` rather than a public address, and out of the FLEET bucket rather than
 * MEDIA, because four properties depend on it: the download is attributable to
 * a device row, a revoked row cannot make it, each environment's worker serves
 * only the build compiled against its own API, and no bucket host is written
 * down outside the `PLATFORM_DOMAIN` derivation. `GET /media/*` is
 * unauthenticated and serves every prefix outside the signed ones, so an APK
 * there would have been public whatever this route did.
 *
 * The response is the APK bytes, so there is no JSON shape to declare and
 * `z.never()` says exactly that: this endpoint never answers a body the typed
 * client could parse. The handler returns a `Response` through the Hono
 * context, which `bindEndpoint` passes through untouched - params are still
 * validated on the way in, which is the whole reason it is declared here at all
 * rather than hand-mounted beside `/print/qr/...`.
 */
export const downloadHandheldRelease = defineEndpoint({
  method: 'GET',
  path: '/b/app/releases/:versionCode/apk',
  mount: 'bakery',
  summary: 'The counter build itself, streamed against the device token (OPS-029)',
  params: HandheldVersionCodeParam,
  response: z.never(),
});

/**
 * The heartbeat a handheld sends on its FLEET token (founder, 2026-09-15: "we
 * always need to see and be able to OTA update our fleet even if they are not
 * paired to any baker").
 *
 * The fleet is not the shop. A Sunmi removed from its counter has no shop token
 * any more, and until this endpoint it therefore sent nothing at all: it
 * vanished from the fleet list and never took another release. It keeps the
 * fleet token `pairDevice` and every `heartbeat` hand it, and beats here - from
 * its pairing screen, and from the closed app's periodic job - so the fleet
 * list keeps reading its telemetry and the offer keeps riding the answer.
 *
 * NO shop, so none of what a shop's beat carries: no counter context, no
 * commands, no configuration. What it reports and what it is answered is the
 * update channel and nothing else. The token names a device row; retiring the
 * device deletes the row and this answers 401 on the next call.
 */
export const fleetHeartbeat = defineEndpoint({
  method: 'POST',
  path: '/b/fleet/heartbeat',
  mount: 'bakery',
  summary: 'A handheld paired to no shop reports itself and is offered builds (OPS-030)',
  body: z.object({
    /** The closed app's periodic job, as on `heartbeat` (BAK-059). */
    screenless: z.boolean().optional(),
    appVersion: z.string().min(1).max(40).optional(),
    /** See `heartbeat.appVersionCode`: the number the offer is compared against. */
    appVersionCode: z.int().positive().max(2100000000).optional(),
    telemetry: HandheldTelemetry.optional(),
  }),
  idempotent: false,
  response: z.object({
    serverTime: Instant,
    /** The same offer `heartbeat` carries, its `apkUrl` on `downloadFleetRelease`. */
    update: HandheldUpdateOffer.nullable(),
  }),
});

/**
 * The build, against the FLEET token - `downloadHandheldRelease` for a handheld
 * that is paired to no shop. The same object, the same refusals (only a
 * published, un-halted build), and the device row read at the moment of the
 * request, so a retired device stops receiving builds immediately (OPS-030).
 */
export const downloadFleetRelease = defineEndpoint({
  method: 'GET',
  path: '/b/fleet/releases/:versionCode/apk',
  mount: 'bakery',
  summary: 'The counter build, streamed against the fleet token (OPS-030)',
  params: HandheldVersionCodeParam,
  response: z.never(),
});

/**
 * A crash, reported on the launch after it (handheld-fleet plan 13).
 *
 * The device wrote the stack, the version, the uptime and the tail of its log
 * ring to a file and let the process die; this is the next launch posting that
 * file. The platform stores it as an OBJECT in the FLEET bucket and keeps only
 * its key on an audit row - no row anywhere holds the document, and the bucket
 * is the one the public media route cannot see (plan 18).
 *
 * Ten per device per service day are stored and the rest are counted and
 * dropped with a 429: a device in a crash loop must not be able to fill a
 * bucket, and the loop itself is already a telemetry alert
 * (`DEVICE_CRASH_LOOP`) that says the same thing more cheaply.
 */
export const reportHandheldCrash = defineEndpoint({
  method: 'POST',
  path: '/b/diagnostics/crash',
  mount: 'bakery',
  summary: 'A counter reports an uncaught exception from its previous run (OPS-029)',
  body: HandheldCrashReport,
  idempotent: false,
  response: HandheldCrashAccepted,
});

/**
 * Everything the counter can say about itself, because an operator asked
 * (plan 13).
 *
 * ONE call, not two: the upload IS the acknowledgement of the
 * `UPLOAD_DIAGNOSTICS` command it names, so a counter cannot upload the
 * evidence and then fail to report that it did - which would leave the command
 * sitting "Delivered" on the operator's page beside the object it produced.
 *
 * The acknowledgement is the only part that can be missed. A bundle naming an
 * id the platform no longer holds, or one naming a command of some other kind,
 * is still stored and audited and answers 200 with `outcome: null` - the
 * document is already in the counter's hand and this request is the only chance
 * to take it.
 */
export const uploadHandheldDiagnostics = defineEndpoint({
  method: 'POST',
  path: '/b/diagnostics/bundle',
  mount: 'bakery',
  summary: 'A counter uploads its log ring, last crash, telemetry and config (OPS-029)',
  body: HandheldDiagnosticsBundle,
  idempotent: false,
  response: HandheldDiagnosticsAccepted,
});
